import { Box, Divider, FlexBox } from '@dixa/lib-shared-habla';

import { MessengerTranslatedLanguage } from 'components/settings/components/sections/dixaMessenger/views/appearance/components/generalSettings/languageSettings/languages';

import {
  ElevioConnectedState,
  ElevioMessengerDisplayType,
  Translatable,
} from '../../../../../../../../messengerSettings.types';
import { ElevioRadioItem } from '../elevioRadioItem/elevioRadioItem';
import { TranslateString } from '../translateString/translateString';

/** Represents active elevio integration state */
export type MessengerElevioSettingsCallbacks = {
  onUpdateDisplayType: (displayType: ElevioMessengerDisplayType) => void;
  onLanguageChange: (language: MessengerTranslatedLanguage) => void;
  onTranslatedStringChange: (
    translatable: Translatable,
    language: MessengerTranslatedLanguage,
    value: string,
  ) => void;
};

type Props = {
  state: ElevioConnectedState;
  selectedLanguage: MessengerTranslatedLanguage;
} & MessengerElevioSettingsCallbacks;

/**
 * Messenger elevio settings component used to configure elevio messenger
 * settings
 */
export function MessengerElevioSettings({
  state: {
    displayType,
    translations: { findArticles, searchBarPlaceholder },
  },
  selectedLanguage,
  onUpdateDisplayType,
  onLanguageChange,
  onTranslatedStringChange,
}: Props): JSX.Element {
  const onValueChange = (translatable: Translatable) => (value: string) => {
    onTranslatedStringChange(translatable, selectedLanguage, value);
  };

  return (
    <div>
      <Box margin="400 0">
        <Divider />
      </Box>

      <FlexBox flexDirection="column" gap="400">
        <ElevioRadioItem
          isActive={displayType === 'display-related-articles-on-the-home-page'}
          label="Display related articles on the home page"
          description="Articles directly related to the page are displayed on the Messenger's front page. They are automatically selected by the system based on the URL."
          onRadioChange={() =>
            onUpdateDisplayType('display-related-articles-on-the-home-page')
          }
        >
          <TranslateString
            key={selectedLanguage}
            selectedLanguage={selectedLanguage}
            inputLabel="Search bar placeholder"
            translatedString={searchBarPlaceholder}
            placeholder="Custom search bar placeholder text"
            onLanguageSelect={onLanguageChange}
            onInputChange={onValueChange('searchBarPlaceholder')}
          />
        </ElevioRadioItem>
        <ElevioRadioItem
          isActive={displayType === 'display-button-for-access-to-articles'}
          label="Show button for access to articles"
          description="Enable a dedicated area for the user to view and search for articles."
          onRadioChange={() =>
            onUpdateDisplayType('display-button-for-access-to-articles')
          }
        >
          <TranslateString
            key={selectedLanguage}
            selectedLanguage={selectedLanguage}
            inputLabel="Button label"
            translatedString={findArticles}
            placeholder="Custom find articles button label"
            onLanguageSelect={onLanguageChange}
            onInputChange={onValueChange('findArticles')}
          />
        </ElevioRadioItem>
      </FlexBox>
    </div>
  );
}
